import { Col, Modal, Row } from 'react-bootstrap';
import { DatePicker, Form, Input, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { Formik, FormikState } from 'formik';
import dayjs from 'dayjs';
import { CreateContractOrderAttivoDto } from '@api/client';
import { MappedOrderOptions } from 'enums/orderOptions';
import { useAppDispatch } from 'core/store/hooks';
import { filterOption } from 'shared/utils/common.utils';
import LinksButton from 'shared/design-system/components/links-button';
import { createClOrder, searchClOrders } from '../../../_redux/actions';
import { AppToastService } from 'shared/design-system/components/app-toast/app-toast.service';

interface Props {
  setShow: (show: boolean) => void;
  show: boolean;
  mappedClOrderOptions: MappedOrderOptions | undefined;
}

const AddClOrderModal: React.FC<Props> = ({ show, setShow, mappedClOrderOptions }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const handleSubmit = (values: CreateContractOrderAttivoDto) => {
    const payload: CreateContractOrderAttivoDto = {
      pkOrdine: values.pkOrdine,
      contractOrderId: values.contractOrderId,
      contractValue: values.contractValue,
      legalEntity: values.legalEntity,
      startDate: values.startDate,
      endDate: values.endDate,
      linkDocumentation: values.linkDocumentation,
      object: values.object,
      note: values.note,
    };
    dispatch(createClOrder(payload))
      .unwrap()
      .then((res) => {
        if (res) {
          setShow(false);
          dispatch(searchClOrders({ searchDto: { paging: { size: 20, page: 0 } } }));
          AppToastService.success(t('cl-orders.order-created'));
        }
      });
  };

  const initialValues: Partial<FormikState<CreateContractOrderAttivoDto>> = {
    values: {
      pkOrdine: undefined,
      contractOrderId: undefined,
      contractValue: undefined,
      legalEntity: undefined,
      startDate: undefined,
      endDate: undefined,
      linkDocumentation: undefined,
      object: undefined,
      note: undefined,
    },
  };

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)} size={'xl'} backdrop="static" keyboard={false}>
        <Modal.Header closeButton={false}>
          <Modal.Title className="title-modal-s" style={{ backgroundColor: 'f3f4f7', fontSize: '1em' }}>
            {t('cl-orders.add-order-title')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik onSubmit={handleSubmit} initialValues={initialValues?.values as CreateContractOrderAttivoDto}>
            {({ handleSubmit: _handleSubmit, handleChange, setFieldValue, values }) => {
              const handleChangeCustom = (input: string) => (e: any) => {
                setFieldValue(input, e);
              };

              const onChangeDate = (input: string) => (date: dayjs.Dayjs) => {
                setFieldValue(input, date ? new Date(date.format('YYYY-MM-DD')) : null);
              };

              return (
                <Form onFinish={_handleSubmit}>
                  <Row>
                    <Col lg={3} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Salesforce Order ID *')}</label>
                        <Form.Item
                          name={t('Salesforce Order ID')}
                          className="mb-3"
                          initialValue={values.pkOrdine}
                          rules={[{ required: true }]}
                        >
                          <Input
                            type="text"
                            name="pkOrdine"
                            placeholder={t('Insert Salesforce Order ID')}
                            value={values.pkOrdine}
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col lg={3} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Contract value')}</label>
                        <Form.Item
                          name={t('Contract value')}
                          className="mb-3"
                          initialValue={values.contractValue}
                        >
                          <Input
                            type="number"
                            name="contractValue"
                            placeholder={t('Insert contract value')}
                            value={values.contractValue}
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Legal Entity')}</label>
                        <Form.Item
                          name={t('Legal Entity')}
                          className="mb-3"
                          initialValue={values.legalEntity}
                        >
                          <Select
                            showSearch
                            className="w-100"
                            optionFilterProp="children"
                            filterOption={filterOption}
                            allowClear
                            placeholder={t('Select legal entity')}
                            onChange={handleChangeCustom('legalEntity')}
                            value={values.legalEntity}
                            getPopupContainer={(triggerMode) => {
                              return triggerMode.parentNode as HTMLElement;
                            }}
                          >
                            {mappedClOrderOptions?.aziendaList?.map((el) => (
                              <Select.Option key={`legalEntity-${el.id}`} value={el.id}>
                                {el.nome}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={3} md={6} sm={6} xs={6}>
                      <div className="form-group">
                        <label>{t('Start date')}</label>
                        <Form.Item
                          name={t('Start date')}
                          className="mb-3"
                          initialValue={values.startDate ? values.startDate : null}
                        >
                          <DatePicker
                            className="w-100"
                            format="DD/MM/YYYY"
                            allowClear
                            placeholder={t('Insert start date')}
                            value={values.startDate ? dayjs(values.startDate) : null}
                            onChange={onChangeDate('startDate')}
                            suffixIcon={<i className="far fa-calendar text-blue-links" />}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={6} xs={6}>
                      <div className="form-group">
                        <label>{t('End date')}</label>
                        <Form.Item
                          name={t('End date')}
                          className="mb-3"
                          initialValue={values.endDate ? dayjs(values.endDate) : null}
                        >
                          <DatePicker
                            className="w-100"
                            format="DD/MM/YYYY"
                            allowClear
                            placeholder={t('Insert end date')}
                            value={values.endDate ? dayjs(values.endDate) : null}
                            onChange={onChangeDate('endDate')}
                            suffixIcon={<i className="far fa-calendar text-blue-links" />}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col lg={3} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Link documentation')}</label>
                        <Form.Item
                          name={t('Link documentation')}
                          className="mb-3"
                          initialValue={values.linkDocumentation}
                        >
                          <Input
                            type="text"
                            name="linkDocumentation"
                            placeholder={t('Insert link documentation')}
                            value={values.linkDocumentation}
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                  <Row className="align-items-center">
                    <Col lg={6} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Object')}</label>
                        <Form.Item name={t('Object')} initialValue={values.object}>
                          <Input.TextArea
                            name="object"
                            placeholder={t('Insert object')}
                            value={values.object}
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col lg={6} md={12} sm={12} xs={12}>
                      <div className="form-group">
                        <label>{t('Notes')}</label>
                        <Form.Item name={t('Notes')} initialValue={values.note}>
                          <Input.TextArea
                            name="note"
                            placeholder={t('Insert notes')}
                            value={values.note}
                            onChange={handleChange}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end">
                    <LinksButton className="btn btn-links-outline" onClick={() => setShow(false)}>
                      {t('common.cancel')}
                    </LinksButton>
                    <LinksButton type="submit" className="btn-primary btn-links">
                      {t('common.add')}
                    </LinksButton>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddClOrderModal;
